import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { registerUser } from '../../adapters/ServiceAPI';
import { User } from '../../sdk/model';

interface RegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  onRegistrationComplete: (user: User) => void;
}

function RegistrationDialog({ open, onClose, onRegistrationComplete }: RegistrationDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const MIN_LENGTH = 3;
  const MAX_LENGTH = 25;

  const isValidLength = username.length >= MIN_LENGTH && username.length <= MAX_LENGTH;
  const canSubmit = isValidLength && !loading;

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canSubmit) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await registerUser(username);
      const user = response.data;
      onRegistrationComplete(user);
    } catch (err: any) {
      console.error('Registration error:', err);
      
      if (err.response) {
        const status = err.response.status;
        const message = err.response.data?.message || err.response.data;
        
        if (status === 400) {
          setError(message || 'Invalid username. Please try another.');
        } else if (status === 409) {
          setError('An account already exists with this email. Please try logging in instead.');
        } else if (status === 401) {
          setError('Your session has expired. Please log in again.');
          onClose();
        } else {
          setError('Registration failed. Please try again.');
        }
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setUsername('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          borderRadius: 3,
          m: isMobile ? 2 : 3,
          maxWidth: isMobile ? 'calc(100% - 32px)' : 480,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonAddIcon sx={{ color: '#4fc3f7', fontSize: 28 }} />
          <Typography
            variant="h5"
            component="span"
            sx={{ fontWeight: 600, color: '#fff' }}
          >
            Complete Your Registration
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="close"
          disabled={loading}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.85)' }}
          >
            Welcome to CAPSized! You've authenticated successfully. 
            Please choose a username to complete your account setup.
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
                color: '#ff8a80',
                '& .MuiAlert-icon': {
                  color: '#ff8a80',
                },
              }}
            >
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            label="Username"
            variant="outlined"
            value={username}
            onChange={handleUsernameChange}
            disabled={loading}
            placeholder="Choose your username"
            helperText={`${username.length}/${MAX_LENGTH} characters (minimum ${MIN_LENGTH})`}
            error={username.length > 0 && !isValidLength}
            InputProps={{
              endAdornment: isValidLength && (
                <InputAdornment position="end">
                  <CheckCircleIcon sx={{ color: '#4caf50' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4fc3f7',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#4fc3f7',
                },
              },
              '& .MuiFormHelperText-root': {
                color: username.length > 0 && !isValidLength 
                  ? '#ff8a80' 
                  : 'rgba(255, 255, 255, 0.5)',
              },
            }}
            inputProps={{
              maxLength: MAX_LENGTH,
            }}
          />

          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
            >
              Your username will be visible to other players in your leagues.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
            sx={{
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #81d4fa 0%, #4fc3f7 100%)',
              },
              '&.Mui-disabled': {
                background: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default RegistrationDialog;


